// @ts-nocheck

import { RootState } from '../index';
import { createSelector } from '@reduxjs/toolkit';

// ------------------------------------ BASE SELECTORS ------------------------------------

export const selectStudyYears = (state: RootState) => state.studyYears;

// ------------------------------------ CORE DATA SELECTORS ------------------------------------

export const selectYears = (state: RootState) => state.studyYears.years;
export const selectCurrentYear = (state: RootState) => state.studyYears.yearSelected;
export const selectCurrentCategory = (state: RootState) => state.studyYears.categorySelected;
export const selectCurrentCompany = (state: RootState) => state.studyYears.companySelected;
export const selectStudyChanges = (state: RootState) => state.studyYears.changes;

// ----------------------------------- RELATED DATA SELECTORS -----------------------------------

export const selectStudyYearPublications = (state: RootState) => state.studyYears.publications;
export const selectStudyYearChanges = (state: RootState) => state.studyYears.changes;
export const selectStudyYearSegments = (state: RootState) => state.studyYears.segments;
export const selectStudyYearCategories = (state: RootState) => state.studyYears.categories;
export const selectStudyYearCompanies = (state: RootState) => state.studyYears.companies;
export const selectStudyYearForecasts = (state: RootState) => state.studyYears.forecasts;

// ------------------------------------ LOADING SELECTORS ------------------------------------

export const selectStudyYearsLoading = (state: RootState) => state.studyYears.isLoading;
export const selectStudyYearsPublishing = (state: RootState) => state.studyYears.isPublishing;
export const selectStudyYearsDownloading = (state: RootState) => state.studyYears.isDownloading;
export const selectStudyYearsUpdatingForecasts = (state: RootState) =>
  state.studyYears.isUpdatingForecasts;

// ------------------------------------ ERROR SELECTORS ------------------------------------

export const selectStudyYearsError = (state: RootState) => state.studyYears.error;
export const selectStudyYearsPublishError = (state: RootState) => state.studyYears.publishError;
export const selectStudyYearsDownloadError = (state: RootState) => state.studyYears.downloadError;
export const selectStudyYearsForecastsError = (state: RootState) => state.studyYears.forecastsError;

// ----------------------------------- SUCCESS SELECTORS -----------------------------------

export const selectStudyYearsPublishSuccess = (state: RootState) => state.studyYears.publishSuccess;
export const selectStudyYearsDownloadSuccess = (state: RootState) =>
  state.studyYears.downloadSuccess;
export const selectStudyYearsForecastsUpdateSuccess = (state: RootState) =>
  state.studyYears.forecastsUpdateSuccess;

// ----------------------------------- DERIVED SELECTORS -----------------------------------

// Check if any loading state is active
export const selectStudyYearsAnyLoading = createSelector(
  [
    selectStudyYearsLoading,
    selectStudyYearsPublishing,
    selectStudyYearsDownloading,
    selectStudyYearsUpdatingForecasts,
  ],
  (isLoading, isPublishing, isDownloading, isUpdatingForecasts) =>
    isLoading || isPublishing || isDownloading || isUpdatingForecasts
);

// Check if any error exists
export const selectStudyYearsAnyError = createSelector(
  [
    selectStudyYearsError,
    selectStudyYearsPublishError,
    selectStudyYearsDownloadError,
    selectStudyYearsForecastsError,
  ],
  (error, publishError, downloadError, forecastsError) =>
    error || publishError || downloadError || forecastsError
);

// Get first available error message
export const selectStudyYearsFirstError = createSelector(
  [
    selectStudyYearsError,
    selectStudyYearsPublishError,
    selectStudyYearsDownloadError,
    selectStudyYearsForecastsError,
  ],
  (error, publishError, downloadError, forecastsError) =>
    error || publishError || downloadError || forecastsError || null
);

// Check if any success flag is active
export const selectStudyYearsAnySuccess = createSelector(
  [
    selectStudyYearsPublishSuccess,
    selectStudyYearsDownloadSuccess,
    selectStudyYearsForecastsUpdateSuccess,
  ],
  (publishSuccess, downloadSuccess, forecastsUpdateSuccess) =>
    publishSuccess || downloadSuccess || forecastsUpdateSuccess
);

// ----------------------------------- FILTERING SELECTORS -----------------------------------

// Get study years by year
export const selectStudyYearsByYear = createSelector(
  [selectYears, (state: RootState, year: number) => year],
  (studyYears, year) => studyYears.filter(sy => sy.year === year)
);

// Get study years by study ID
export const selectStudyYearsByStudyId = createSelector(
  [selectYears, (state: RootState, studyId: string) => studyId],
  (studyYears, studyId) => studyYears.filter(sy => sy.study_id === studyId)
);

// Get study years by status
export const selectStudyYearsByStatus = createSelector(
  [selectYears, (state: RootState, status: string) => status],
  (studyYears, status) => studyYears.filter(sy => sy.status === status)
);

// ---------------------------------- COMPANIES SELECTORS ----------------------------------

// Get companies by revenue range
export const selectCompaniesByRevenueRange = createSelector(
  [
    selectStudyYearCompanies,
    (state: RootState, minRevenue: number) => minRevenue,
    (state: RootState, minRevenue: number, maxRevenue: number) => maxRevenue,
  ],
  (companies, minRevenue, maxRevenue) =>
    companies.filter(company => company.revenue >= minRevenue && company.revenue <= maxRevenue)
);

// Get top companies by revenue
export const selectTopCompaniesByRevenue = createSelector(
  [selectStudyYearCompanies, (state: RootState, limit: number) => limit],
  (companies, limit) => [...companies].sort((a, b) => b.revenue - a.revenue).slice(0, limit)
);

// Get companies by status
export const selectCompaniesByStatus = createSelector(
  [selectStudyYearCompanies, (state: RootState, status: string) => status],
  (companies, status) => companies.filter(company => company.revenue_status === status)
);

// ---------------------------------- SEGMENTS SELECTORS ----------------------------------

// Get segments by category
export const selectSegmentsByCategory = createSelector(
  [selectStudyYearSegments, (state: RootState, categoryId: string) => categoryId],
  (segments, categoryId) => segments.filter(segment => segment.category_id === categoryId)
);

// Get segments by name pattern
export const selectSegmentsByNamePattern = createSelector(
  [selectStudyYearSegments, (state: RootState, pattern: string) => pattern],
  (segments, pattern) =>
    segments.filter(segment => segment.category_name?.toLowerCase().includes(pattern.toLowerCase()))
);

// ---------------------------------- FORECASTS SELECTORS ----------------------------------

// Get forecasts by year
export const selectForecastsByYear = createSelector(
  [selectStudyYearForecasts, (state: RootState, year: number) => year],
  (forecasts, year) => forecasts.filter(forecast => forecast.forecast_year === year)
);

// Get forecasts by growth rate
export const selectForecastsByGrowthRate = createSelector(
  [selectStudyYearForecasts, (state: RootState, minGrowth: number) => minGrowth],
  (forecasts, minGrowth) =>
    forecasts.filter(forecast => forecast.yoy_revenue_percentage >= minGrowth)
);

// ----------------------------------- ANALYTICS SELECTORS -----------------------------------

// Calculate total revenue across all companies
export const selectTotalRevenue = createSelector([selectStudyYearCompanies], companies =>
  companies.reduce((total, company) => total + company.revenue, 0)
);

// Calculate average revenue per company
export const selectAverageRevenue = createSelector([selectStudyYearCompanies], companies => {
  if (companies.length === 0) return 0;
  return companies.reduce((total, company) => total + company.revenue, 0) / companies.length;
});

// Get revenue distribution stats
export const selectRevenueStats = createSelector([selectStudyYearCompanies], companies => {
  if (companies.length === 0) {
    return { min: 0, max: 0, avg: 0, total: 0, count: 0 };
  }

  const revenues = companies.map(c => c.revenue);
  return {
    min: Math.min(...revenues),
    max: Math.max(...revenues),
    avg: revenues.reduce((sum, rev) => sum + rev, 0) / revenues.length,
    total: revenues.reduce((sum, rev) => sum + rev, 0),
    count: companies.length,
  };
});

// ----------------------------------- UTILITY SELECTORS -----------------------------------

// Check if data is ready (not loading and no errors)
export const selectStudyYearsDataReady = createSelector(
  [selectStudyYearsAnyLoading, selectStudyYearsAnyError],
  (isLoading, hasError) => !isLoading && !hasError
);

// Get current study year with enriched data
export const selectCurrentYearEnriched = createSelector(
  [selectCurrentYear, selectStudyYearCompanies, selectStudyYearSegments, selectStudyYearForecasts],
  (currentYear, companies, segments, forecasts) => {
    if (!currentYear) return null;

    return {
      ...currentYear,
      companiesCount: companies.length,
      segmentsCount: segments.length,
      forecastsCount: forecasts.length,
      totalRevenue: companies.reduce((total, company) => total + company.revenue, 0),
    };
  }
);

// Get study year summary data
export const selectStudyYearSummary = createSelector([selectYears], studyYears => ({
  total: studyYears.length,
  byYear: studyYears.reduce(
    (acc, sy) => {
      acc[sy.year] = (acc[sy.year] || 0) + 1;
      return acc;
    },
    {} as Record<number, number>
  ),
  byStatus: studyYears.reduce(
    (acc, sy) => {
      const status = sy.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  ),
}));
